import React, { useState } from 'react';
import './RequestInquireModal.css';

const RequestInquireModal = ({ 
  isOpen, 
  onClose, 
  onReply, 
  onUpgradePlan,
  onStatusChange,
  inquireData = {},
  isSubscribed = false, // Whether seller has premium subscription
  isLoading = false // Loading state for reply
}) => {
  const [replyMessage, setReplyMessage] = useState('');
  const [showReplyBox, setShowReplyBox] = useState(false);

  const {
    id,
    quantity,
    unit = 'Units',
    offerPrice,
    currency = 'USD',
    email,
    phoneNumber,
    fullName,
    companyName,
    country,
    category,
    subCategory,
    details,
    status,
    createdAt
  } = inquireData;

  // Mask sensitive data if not subscribed
  const maskEmail = (email) => {
    if (!email) return '**********************';
    if (isSubscribed) return email;
    const [name, domain] = email.split('@');
    return `${name.substring(0, 2)}${'*'.repeat(name.length - 2)}@${'*'.repeat(domain?.length || 5)}`;
  };

  const maskPhone = (phone) => {
    if (!phone) return '**********************';
    if (isSubscribed) return phone;
    return phone.substring(0, 4) + '*'.repeat(phone.length - 4);
  };

  const formatPrice = (price, curr) => {
    if (!price) return 'Not specified';
    const symbol = curr === 'USD' ? '$' : curr === 'EUR' ? '€' : curr === 'AED' ? 'AED ' : '$';
    return `${symbol}${price}`;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleLater = () => {
    setShowReplyBox(false);
    setReplyMessage('');
    if (onClose) {
      onClose();
    }
  };

  const handleReplyClick = () => {
    if (isSubscribed) {
      setShowReplyBox(true);
    } else {
      if (onUpgradePlan) {
        onUpgradePlan();
      }
    }
  };

  const handleSendReply = () => {
    if (onReply && replyMessage.trim() && !isLoading) {
      onReply({
        rfqId: id,
        message: replyMessage,
        buyerEmail: email,
        buyerName: fullName
      });
      // Don't close modal here - parent will close after success
      // and will handle status update
      setReplyMessage('');
      setShowReplyBox(false);
    }
  };

  const handleUpgradePlan = () => {
    if (onUpgradePlan) {
      onUpgradePlan();
    }
  };

  const getStatusBadgeClass = (status) => {
    switch(status) {
      case 'new': return 'status-new';
      case 'replied': return 'status-replied';
      case 'closed': return 'status-closed';
      default: return '';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="request-inquire-modal-overlay" onClick={onClose}>
      <div className="request-inquire-modal" onClick={(e) => e.stopPropagation()}>
        <div className="request-inquire-content">
          <div className="request-inquire-header">
            <div className="request-inquire-title-row">
              <h2 className="request-inquire-title">Request for Inquire</h2>
              {status && (
                <span className={`request-inquire-status ${getStatusBadgeClass(status)}`}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </span>
              )}
            </div>
            
            {category && (
              <p className="request-inquire-category">
                {category} {subCategory && `> ${subCategory}`}
              </p>
            )}
            
            {createdAt && (
              <p className="request-inquire-date">Submitted: {formatDate(createdAt)}</p>
            )}
            
            <div className="request-inquire-details-section">
              <div className="request-inquire-details-list">
                {quantity && (
                  <p className="request-inquire-detail-item">
                    <span className="request-inquire-detail-label">Quantity:</span>
                    <span className="request-inquire-detail-value"> {quantity} {unit}</span>
                  </p>
                )}
                
                <p className="request-inquire-detail-item">
                  <span className="request-inquire-detail-label">Offer price: </span>
                  <span className="request-inquire-detail-value">{formatPrice(offerPrice, currency)}</span>
                </p>
                
                <p className="request-inquire-detail-item">
                  <span className="request-inquire-detail-label">Email ID: </span>
                  <span className={`request-inquire-detail-value ${!isSubscribed ? 'masked' : ''}`}>
                    {maskEmail(email)}
                  </span>
                </p>

                <p className="request-inquire-detail-item">
                  <span className="request-inquire-detail-label">Phone: </span>
                  <span className={`request-inquire-detail-value ${!isSubscribed ? 'masked' : ''}`}>
                    {maskPhone(phoneNumber)}
                  </span>
                </p>

                {isSubscribed && (
                  <>
                    <p className="request-inquire-detail-item">
                      <span className="request-inquire-detail-label">Contact: </span>
                      <span className="request-inquire-detail-value">{fullName}</span>
                    </p>
                    
                    <p className="request-inquire-detail-item">
                      <span className="request-inquire-detail-label">Company: </span>
                      <span className="request-inquire-detail-value">{companyName}</span>
                    </p>
                    
                    <p className="request-inquire-detail-item">
                      <span className="request-inquire-detail-label">Country: </span>
                      <span className="request-inquire-detail-value">{country}</span>
                    </p>
                  </>
                )}
                
                <p className="request-inquire-detail-item details-text">
                  <span className="request-inquire-detail-label">Details: </span>
                  <span className={`request-inquire-detail-value ${!isSubscribed ? 'masked-details' : ''}`}>
                    {isSubscribed ? details : 'Upgrade your package to view full buyer details and send your quotation. Upgrade your package to view full buyer details and send your quotation.'}
                  </span>
                </p>
              </div>
              
              {!isSubscribed && (
                <p className="request-inquire-upgrade-message">
                  Upgrade your package to view full buyer details and send your quotation.
                </p>
              )}
            </div>
            
            {!isSubscribed && (
              <button 
                className="request-inquire-upgrade-link"
                onClick={handleUpgradePlan}
              >
                Upgrade Plan
              </button>
            )}

            {showReplyBox && isSubscribed && (
              <div className="request-inquire-reply-section">
                <textarea
                  className="request-inquire-reply-textarea"
                  placeholder="Type your reply message here..."
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  rows={4}
                  disabled={isLoading}
                />
                <button 
                  className="request-inquire-send-btn"
                  onClick={handleSendReply}
                  disabled={!replyMessage.trim() || isLoading}
                >
                  {isLoading ? 'Sending...' : 'Send Reply'}
                </button>
              </div>
            )}
          </div>
        </div>
        
        <div className="request-inquire-buttons">
          <button 
            className="request-inquire-later-btn"
            onClick={handleLater}
          >
            Later
          </button>
          <button 
            className="request-inquire-reply-btn"
            onClick={handleReplyClick}
          >
            {showReplyBox ? 'Cancel' : 'Reply'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestInquireModal;
